import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { CheckCircle2, MapPin, Phone, Mail, Home } from "lucide-react";
import { API } from "@/lib/utils";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_pool-lesson-book/artifacts/c9s2nl0s_rr%20logo.png";

export default function ConfirmationPage() {
  const { registrationId } = useParams();
  const navigate = useNavigate();
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRegistration();
  }, [registrationId]);

  const fetchRegistration = async () => {
    try {
      const res = await axios.get(`${API}/registration/${registrationId}`);
      setRegistration(res.data);
    } catch (err) {
      console.error("Failed to fetch registration:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#109FE9] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!registration) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="text-center">
          <p className="text-slate-600 mb-4">Registration not found.</p>
          <Button onClick={() => navigate("/")} className="btn-primary">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  const statusBadge = () => {
    switch (registration.status) {
      case "paid":
        return <span className="badge-green">Paid - Confirmed</span>;
      case "signed":
        return <span className="badge-yellow">Signed - Awaiting Payment</span>;
      default:
        return <span className="badge-yellow">Reserved</span>;
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <div className="pattern-bg" />
      
      <header className="bg-white shadow-sm border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <img src={LOGO_URL} alt="Rolling River Day Camp" className="h-14" />
          <Button variant="ghost" onClick={() => navigate("/")} className="text-slate-600">
            <Home className="w-4 h-4 mr-2" /> Home
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <div className="card animate-fade-in text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-12 h-12 text-green-600" />
          </div>

          <h1 className="font-heading text-3xl font-bold text-slate-800 mb-2">
            Registration Complete!
          </h1>
          <p className="text-slate-600 mb-6">
            Thank you for registering for swim lessons at Rolling River Day Camp.
          </p>

          <div className="mb-6">
            {statusBadge()}
          </div>

          {/* Registration Details */}
          <div className="bg-slate-50 rounded-xl p-6 text-left mb-6">
            <h2 className="font-heading font-bold text-lg text-slate-800 mb-4">
              {registration.child_first_name} {registration.child_last_name}
            </h2>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Mail className="w-4 h-4 text-slate-600" />
              </div>
              <p className="text-sm text-slate-600 pt-1">
                Confirmation sent to: {registration.email}
              </p>
            </div>
          </div>

          {/* Lesson Details */}
          <div className="bg-[#E6F5FD] border-2 border-[#109FE9] rounded-xl p-6 text-left mb-6" data-testid="lesson-details">
            <h3 className="font-heading font-bold text-[#0E8ACB] mb-1">Lesson Details</h3>
            <p className="text-slate-700 font-semibold">
              {registration.confirmed_day?.charAt(0).toUpperCase() + registration.confirmed_day?.slice(1)} at {registration.confirmed_time}
            </p>
            <p className="text-sm text-slate-600 mt-1">
              Lifeguard: {registration.assigned_instructor || registration.lifeguard_preference}
            </p>
            <p className="text-2xl font-bold text-[#0E8ACB] my-2" data-testid="lesson-price">
              ${registration.confirmed_price?.toFixed(2) || registration.choice1_price?.toFixed(2)}
            </p>
            <p className="text-xs text-slate-500 mb-3">
              {(registration.confirmed_dates || registration.choice1_dates)?.length} lessons
            </p>
            <div className="flex flex-wrap gap-2">
              {(registration.confirmed_dates || registration.choice1_dates)?.map((date, idx) => (
                <span key={idx} className="px-3 py-1 bg-white rounded-full text-sm text-slate-700">
                  {date}
                </span>
              ))}
            </div>
          </div>

          {/* Payment Instructions */}
          {registration.status !== "paid" && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-left mb-6">
              <h3 className="font-semibold text-yellow-800 mb-2">Payment Instructions</h3>
              <p className="text-sm text-yellow-700">
                All lessons must be paid <strong>in cash</strong> in person at the camp office within <strong>7 days</strong>. 
                If payment is not received within 7 days, your reserved spot will be released.
              </p>
              <div className="mt-4 flex items-center gap-2 text-sm text-yellow-800">
                <MapPin className="w-4 h-4" />
                <span>Rolling River Day Camp Office</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-yellow-800">
                <Phone className="w-4 h-4" />
                <span>(516) 593-2267</span>
              </div>
            </div>
          )}

          {registration.status === "paid" && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-left mb-6">
              <h3 className="font-semibold text-green-800 mb-2">Payment Confirmed!</h3>
              <p className="text-sm text-green-700">
                Your payment has been received. Your spot is confirmed for the summer session. 
                We look forward to seeing you at camp!
              </p>
            </div>
          )}

          <Button onClick={() => navigate("/")} className="btn-primary">
            <Home className="w-4 h-4 mr-2" /> Return to Home
          </Button>
        </div>
      </main>
    </div>
  );
}
